import { flow } from 'fp-ts/function';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { includes, isEmpty, memoize, upperFirst } from 'lodash/fp';
import validate from 'validate.js';

import { CheckoutField, TextKey } from '@mews-distributor/common';
import { PaymentCard } from '@mews/framework';

const lowerCaseNotEquals = (a: string) => (b: string) => a.toLowerCase() !== b.toLowerCase();

const validatePhoneNumber = (value: string, isPhoneInputPrototypeEnabled: boolean) => {
    if (!isPhoneInputPrototypeEnabled || value == null || value.length === 0) {
        return true;
    }
    return isValidPhoneNumber(value);
};

validate.validators.truth = (value: boolean, options: $TSFixMe) => (value !== true ? options.message : null);

validate.validators.number = (value: number, options: $TSFixMe) =>
    PaymentCard.validateCardNumber(value) ? null : options.message;

validate.validators.numberType = (value: $TSFixMe, options: $TSFixMe) => {
    const card = PaymentCard.cardFromNumber(value);
    // card is recognized but not supported
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    if (card && card.type && options.type.every(lowerCaseNotEquals(card.type))) {
        return options.message(card.name);
    }
    return null;
};

validate.validators.expiration = (value = '', options: $TSFixMe) => {
    const { month, year } = PaymentCard.cardExpiryValue(value);
    return PaymentCard.validateCardExpiry(month.toString(), year.toString()) ? null : options.message;
};

validate.validators.cvv = (value = '', options: $TSFixMe) =>
    PaymentCard.validateCardCVC(value) ? null : options.message;

validate.validators.pciProxyValue = (
    __: unknown,
    { metadata: { valid = false, length = 0 } = {}, message }: $TSFixMe
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
) => (length > 0 && valid ? null : message);
validate.validators.pciProxyCardType = (
    __: unknown,
    { metadata: { types = [], type = null } = {}, message }: $TSFixMe
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
) => (type && types.every(lowerCaseNotEquals(type)) ? message(upperFirst(type)) : null);

validate.validators.optionalEmail = (value: $TSFixMe, { message }: $TSFixMe) =>
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    value ? validate.single(value, { email: { message } }) : null;

validate.validators.presence.options = { allowEmpty: false };

interface WithMessage {
    message: string;
}

validate.validators.optionalPhone = (
    value: string,
    { message, isPhoneInputPrototypeEnabled }: { isPhoneInputPrototypeEnabled: boolean } & WithMessage
) => (validatePhoneNumber(value, isPhoneInputPrototypeEnabled) ? null : message);

const required = { message: 'ValidateRequired' };
const email = { message: 'ValidateEmail' };
const getPhoneValidator = (isPhoneInputPrototypeEnabled: boolean) => ({
    isPhoneInputPrototypeEnabled,
    message: 'ValidatePhoneNumber',
});

const getRequiredFieldValidator = (fieldName: $TSFixMe) =>
    flow(includes(fieldName), (isRequired) => (isRequired ? required : null));

const getRequiredPhoneValidator = getRequiredFieldValidator(CheckoutField.Telephone);
const getRequiredEmailValidator = getRequiredFieldValidator(CheckoutField.Email);

export const getCheckoutFormConstraints = ({
    requiredFields,
    isPhoneInputPrototypeEnabled,
}: {
    requiredFields: $TSFixMe;
    isPhoneInputPrototypeEnabled: boolean;
}) => ({
    [CheckoutField.Email]: {
        presence: getRequiredEmailValidator(requiredFields),
        optionalEmail: email,
    },

    [CheckoutField.LastName]: { presence: required },

    [CheckoutField.AgreeWithConditions]: {
        truth: { message: 'ValidateAgreement' },
    },

    [CheckoutField.Telephone]: {
        presence: getRequiredPhoneValidator(requiredFields),
        optionalPhone: getPhoneValidator(isPhoneInputPrototypeEnabled),
    },
});

const expiration = {
    presence: required,
    expiration: { message: TextKey.ValidatePaymentCardExpiration },
};

const holderName = {
    presence: required,
};

export const getCreditCardFormConstraints = memoize(({ supportedCreditCardTypes }) => ({
    [CheckoutField.CardHolderName]: holderName,
    [CheckoutField.CardExpiration]: expiration,
    [CheckoutField.CardNumber]: {
        presence: required,
        number: { message: TextKey.ValidatePaymentCard },
        numberType: {
            message: (type: $TSFixMe) => ({
                key: TextKey.ValidatePaymentCardType,
                substitutes: { type },
            }),
            types: supportedCreditCardTypes,
        },
    },
    [CheckoutField.CardCVV]: {
        presence: required,
        cvv: { message: 'ValidateCVV' },
    },
}));

export const getPciProxyPaymentCardConstraints = ({ number, cvv, supportedCreditCardTypes }: $TSFixMe) => ({
    [CheckoutField.CardHolderName]: holderName,
    [CheckoutField.CardExpiration]: expiration,
    [CheckoutField.CardNumber]: {
        presence: required,
        pciProxyValue: {
            metadata: number,
            message: TextKey.ValidatePaymentCard,
        },
        pciProxyCardType: {
            metadata: { ...number, types: supportedCreditCardTypes },
            message: (type: $TSFixMe) =>
                isEmpty(type)
                    ? TextKey.ValidatePaymentCard
                    : { key: TextKey.ValidatePaymentCardType, substitutes: { type } },
        },
    },
    [CheckoutField.CardCVV]: {
        presence: required,
        pciProxyValue: {
            metadata: cvv,
            message: 'ValidateCVV',
        },
    },
});

export const getBookerConstraints = (
    {
        customerEmail,
        isPhoneInputPrototypeEnabled,
    }: { customerEmail: string | undefined; isPhoneInputPrototypeEnabled: boolean },
    requiredFields: unknown[]
) => ({
    [CheckoutField.BookerEmail]: {
        presence: required,
        email,
        exclusion: {
            within: [customerEmail],
            message: 'ValidateBookerEmail',
        },
    },

    [CheckoutField.BookerLastName]: {
        presence: required,
    },

    [CheckoutField.BookerTelephone]: {
        presence: getRequiredPhoneValidator(requiredFields),
        optionalPhone: getPhoneValidator(isPhoneInputPrototypeEnabled),
    },
});

export const baseRequiredFields = [
    CheckoutField.LastName,
    CheckoutField.BookerLastName,
    CheckoutField.BookerEmail,
    CheckoutField.CardNumber,
    CheckoutField.CardExpiration,
    CheckoutField.CardCVV,
    CheckoutField.CardHolderName,
    CheckoutField.AgreeWithConditions,
];

export const configurableRequiredFields = {
    [CheckoutField.Telephone]: [CheckoutField.Telephone, CheckoutField.BookerTelephone],
};
